import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Wallet, Pencil, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => (typeof n === "number" ? n.toFixed(2) : "0.00");
const monthLabel = (iso) => {
  if (!iso) return "—";
  const [y, m] = iso.split("-");
  return new Date(parseInt(y), parseInt(m) - 1, 1)
    .toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};

export default function LeaveLedger() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMonth, setFilterMonth] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // {employee_id, name, code, month, opening_el, opening_pl}

  const load = async () => {
    setLoading(true);
    try {
      const params = filterMonth ? { month: `${filterMonth}-01` } : {};
      const { data } = await api.get("/attendance/leave-ledger", { params });
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterMonth]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      String(r.employee_name || "").toLowerCase().includes(q) ||
      String(r.punch_id || "").toLowerCase().includes(q) ||
      String(r.employee_code || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const openEdit = (r) => setEditing({
    employee_id: r.employee_id,
    name: r.employee_name,
    code: r.employee_code,
    punch_id: r.punch_id,
    month: r.month,
    opening_el: r.opening_override_el ?? r.opening_el ?? 0,
    opening_pl: r.opening_override_pl ?? r.opening_pl ?? 0,
    manual_used_el: r.manual_used_el ?? r.used_el ?? 0,
    manual_used_pl: r.manual_used_pl ?? r.used_pl ?? 0,
    manual_accrued_el: r.manual_accrued_el ?? 0,
    manual_accrued_pl: r.manual_accrued_pl ?? 0,
    reason: "",
  });

  const save = async () => {
    if (!editing) return;
    try {
      await api.post("/attendance/leave-ledger/opening", {
        employee_id: editing.employee_id,
        month: editing.month,
        opening_el: parseFloat(editing.opening_el) || 0,
        opening_pl: parseFloat(editing.opening_pl) || 0,
        manual_accrued_el: parseFloat(editing.manual_accrued_el) || 0,
        manual_accrued_pl: parseFloat(editing.manual_accrued_pl) || 0,
        manual_used_el: parseFloat(editing.manual_used_el) || 0,
        manual_used_pl: parseFloat(editing.manual_used_pl) || 0,
        reason: editing.reason || null,
      });
      toast.success("Ledger entry saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Rev. 2026-08-04 — Per-employee re-accrue (fixes the case where accrual
  // cron ran BEFORE HR toggled el_applicable / pl_applicable ON)
  // Rev. 2026-08-05 — Diagnostic-driven flow. Auto-enables eligibility.
  // If +EL / +PL still 0, shows the exact reason from the backend and
  // (for probation blocks) offers a one-click bypass retry.
  const reAccrue = async (row) => {
    if (!window.confirm(
      `Re-accrue EL/PL for ${row.name} (${row.punch_id}) for ${row.month}?\n\n` +
      "This will:\n" +
      " • Auto-enable EL/PL flags on the employee if OFF\n" +
      " • Delete this month's accrual log to break idempotency\n" +
      " • Re-run the monthly accrual (only for this employee)\n\n" +
      "Manual adjustments (opening balance, used) are PRESERVED.\n\n" +
      "Proceed?",
    )) return;
    try {
      const { data } = await api.post("/leave-config/re-accrue-employee", {
        employee_id: row.employee_id,
        month: row.month,
        enable_eligibility: true,
        bypass_probation: false,
      });
      const el = Number(data.new_accrued_el || 0).toFixed(2);
      const pl = Number(data.new_accrued_pl || 0).toFixed(2);
      const elReason = data?.diagnostic?.el?.reason || "";
      const plReason = data?.diagnostic?.pl?.reason || "";
      const elBlocked = Number(data.new_accrued_el || 0) === 0;
      const plBlocked = Number(data.new_accrued_pl || 0) === 0;

      if (elBlocked || plBlocked) {
        // Show diagnostic in an alert so HR sees the reason.
        const lines = [
          `Re-accrue done for ${row.name}:`,
          `  +EL = ${el}   ${elReason ? "→ " + elReason : ""}`,
          `  +PL = ${pl}   ${plReason ? "→ " + plReason : ""}`,
        ];
        // Detect probation block and offer instant bypass retry.
        const bothProbationBlocked =
          (elBlocked && /probation/i.test(elReason)) ||
          (plBlocked && /probation/i.test(plReason));
        if (bothProbationBlocked) {
          lines.push("");
          lines.push("Click OK to RETRY with 'Bypass probation' for this employee only.");
          if (window.confirm(lines.join("\n"))) {
            const retry = await api.post("/leave-config/re-accrue-employee", {
              employee_id: row.employee_id,
              month: row.month,
              enable_eligibility: true,
              bypass_probation: true,
            });
            const el2 = Number(retry.data.new_accrued_el || 0).toFixed(2);
            const pl2 = Number(retry.data.new_accrued_pl || 0).toFixed(2);
            toast.success(`Re-accrued (probation bypassed) — +EL ${el2}, +PL ${pl2}`);
            load();
            return;
          }
        } else {
          window.alert(lines.join("\n"));
        }
      } else {
        toast.success(`Re-accrued — new +EL ${el}, +PL ${pl}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="leave-ledger-page" className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <Wallet className="h-7 w-7 text-slate-500" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase 5</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            EL & PL Leave Ledger
          </h1>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-3 flex-wrap">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Month</Label>
          <Input
            type="month"
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            className="w-44"
            data-testid="ledger-month-filter"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Search</Label>
          <Input
            placeholder="Punch ID, code, or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="ledger-search"
          />
        </div>
        <div className="text-xs text-slate-500 ml-auto">
          {filtered.length} row{filtered.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-xs min-w-[1100px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Employee</th>
              <th className="text-left px-3 py-2">Month</th>
              <th className="text-right px-3 py-2">EL Open</th>
              <th className="text-right px-3 py-2">PL Open</th>
              <th className="text-right px-3 py-2 text-green-700">+ EL</th>
              <th className="text-right px-3 py-2 text-green-700">+ PL</th>
              <th className="text-right px-3 py-2">Shortfall</th>
              <th className="text-right px-3 py-2 text-red-700">- EL</th>
              <th className="text-right px-3 py-2 text-red-700">- PL</th>
              <th className="text-right px-3 py-2 text-red-700 font-semibold">LOP</th>
              <th className="text-right px-3 py-2 font-semibold">EL Close</th>
              <th className="text-right px-3 py-2 font-semibold">PL Close</th>
              <th className="px-3 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan="13" className="text-center py-8 text-slate-400">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="13" className="text-center py-10 text-slate-400">No ledger entries{filterMonth ? " for this month" : ""}.</td></tr>
            )}
            {!loading && filtered.map((r) => {
              const isOverride = r.opening_override_el !== null && r.opening_override_el !== undefined;
              return (
                <tr key={`${r.employee_id}-${r.month}`} data-testid={`ledger-row-${r.employee_id}-${r.month}`} className="hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{r.employee_name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{r.punch_id} · {r.employee_code}</div>
                  </td>
                  <td className="px-3 py-2">{monthLabel(r.month)}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {fmt(r.opening_el)}
                    {isOverride && <span className="ml-1 text-[9px] uppercase text-amber-700 bg-amber-100 px-1 rounded">man</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(r.opening_pl)}</td>
                  <td className="px-3 py-2 text-right font-mono text-green-700">{fmt(r.accrued_el)}</td>
                  <td className="px-3 py-2 text-right font-mono text-green-700">{fmt(r.accrued_pl)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(r.shortfall)}</td>
                  <td className="px-3 py-2 text-right font-mono text-red-700">{fmt(r.used_el)}</td>
                  <td className="px-3 py-2 text-right font-mono text-red-700">{fmt(r.used_pl)}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">
                    {r.lop_days > 0 ? <span className="text-red-700">{fmt(r.lop_days)}</span> : "0.00"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(r.closing_el)}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(r.closing_pl)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => reAccrue(r)}
                        data-testid={`ledger-reaccrue-${r.employee_id}-${r.month}`}
                        className="h-7 px-2 text-emerald-700 hover:bg-emerald-50"
                        title="Force re-accrue this month (use when EL/PL flags were flipped after cron ran)"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(r)}
                        data-testid={`ledger-edit-${r.employee_id}-${r.month}`}
                        className="h-7 px-2"
                        title="Set opening balance / manual override"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md" data-testid="ledger-edit-dialog">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle>Edit ledger — {editing.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-xs text-slate-500">
                  {editing.punch_id} · {editing.code} · Month: <span className="font-medium">{monthLabel(editing.month)}</span>
                </div>
                <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded p-3">
                  <strong>Auto-mode is OFF.</strong> No EL/PL is auto-credited or auto-deducted.
                  HR enters Opening, Accrual (manual credit this month), and Used values directly.
                  Closing = Opening + Accrual − Used.
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-slate-500">Opening EL</Label>
                    <Input type="number" step="0.25" min="0" value={editing.opening_el}
                      onChange={(e) => setEditing({ ...editing, opening_el: e.target.value })}
                      data-testid="opening-el-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-slate-500">Opening PL</Label>
                    <Input type="number" step="0.25" min="0" value={editing.opening_pl}
                      onChange={(e) => setEditing({ ...editing, opening_pl: e.target.value })}
                      data-testid="opening-pl-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-green-700">+ Accrued EL</Label>
                    <Input type="number" step="0.25" min="0" value={editing.manual_accrued_el}
                      onChange={(e) => setEditing({ ...editing, manual_accrued_el: e.target.value })}
                      data-testid="accrued-el-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-green-700">+ Accrued PL</Label>
                    <Input type="number" step="0.25" min="0" value={editing.manual_accrued_pl}
                      onChange={(e) => setEditing({ ...editing, manual_accrued_pl: e.target.value })}
                      data-testid="accrued-pl-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-red-700">− Used EL</Label>
                    <Input type="number" step="0.25" min="0" value={editing.manual_used_el}
                      onChange={(e) => setEditing({ ...editing, manual_used_el: e.target.value })}
                      data-testid="used-el-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-red-700">− Used PL</Label>
                    <Input type="number" step="0.25" min="0" value={editing.manual_used_pl}
                      onChange={(e) => setEditing({ ...editing, manual_used_pl: e.target.value })}
                      data-testid="used-pl-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">Reason / note (optional)</Label>
                  <Input placeholder="e.g., Casual leave on Apr 10 + Apr 15"
                    value={editing.reason}
                    onChange={(e) => setEditing({ ...editing, reason: e.target.value })}
                    data-testid="opening-reason-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={save} className="brand-bg brand-bg-hover text-white" data-testid="opening-save-btn">
                  Save ledger entry
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
